import { MarketPlace } from '@amzn/fire-tv-campaign-automation-service';

import { ProvisionedConstants, STAGE } from './constants';

// ─── Flag definitions ─────────────────────────────────────────────────────────

export enum FeatureFlag {
  RdpIntegration = 'rdpIntegration',
  EmbeddedMode = 'embeddedMode',
  OverlaySection = 'overlaySection',
}

// ─── Stage configuration ──────────────────────────────────────────────────────

const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.RdpIntegration]: false,
  [FeatureFlag.EmbeddedMode]: false,
  [FeatureFlag.OverlaySection]: false,
};

const STAGE_OVERRIDES: Partial<Record<STAGE, Partial<Record<FeatureFlag, boolean>>>> = {
  [STAGE.DEV]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: true,
  },
  [STAGE.BETA]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: true,
  },
  [STAGE.GAMMA]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: true,
  },
  [STAGE.PROD_NA]: {
    [FeatureFlag.RdpIntegration]: true,
    [FeatureFlag.EmbeddedMode]: true,
    [FeatureFlag.OverlaySection]: false,
  },
};

// ─── Marketplace configuration ────────────────────────────────────────────────

/**
 * Narrows a feature flag to specific marketplaces after the stage gate passes.
 *
 * Use one of:
 *  - `allowedMarketplaces` — flag is ON only for these marketplaces (allowlist).
 *  - `deniedMarketplaces`  — flag is OFF for these marketplaces, ON for all others (denylist).
 *
 * When both are set, `allowedMarketplaces` takes precedence.
 * If neither is set, the stage result is returned unchanged.
 * If the stage already resolved the flag to false, no marketplace config can re-enable it.
 */
export interface MarketplaceOverrideConfig {
  allowedMarketplaces?: MarketPlace[];
  deniedMarketplaces?: MarketPlace[];
}

/**
 * Per-flag marketplace restrictions, evaluated after the stage gate.
 * Omitting a flag here means it is available in all marketplaces (stage permitting).
 *
 * To add a restriction, add an entry:
 * @example
 * [FeatureFlag.OverlaySection]: { allowedMarketplaces: [MarketPlace.US, MarketPlace.UK] }
 */
const MARKETPLACE_OVERRIDES: Partial<Record<FeatureFlag, MarketplaceOverrideConfig>> = {
  [FeatureFlag.RdpIntegration]: { allowedMarketplaces: [MarketPlace.US] },
};

// ─── Evaluation ───────────────────────────────────────────────────────────────

const resolveMarketplace = (
  stageResult: boolean,
  marketplace: MarketPlace,
  override: MarketplaceOverrideConfig | undefined,
): boolean => {
  if (!stageResult || !override) return stageResult;

  if (override.allowedMarketplaces) {
    return override.allowedMarketplaces.includes(marketplace);
  }

  if (override.deniedMarketplaces) {
    return !override.deniedMarketplaces.includes(marketplace);
  }

  return stageResult;
};

/**
 * Returns whether a feature flag is enabled for the current stage and, optionally,
 * a specific marketplace.
 *
 * Prefer the `useFeatureFlag` hook inside React components — it reads the
 * marketplace from context automatically.
 *
 * @param flag        - The feature flag to evaluate.
 * @param marketplace - Optional. When omitted, only the stage gate applies.
 *
 * @example
 * // Outside React (configs, utilities)
 * isFeatureEnabled(FeatureFlag.EmbeddedMode)
 *
 * @example
 * // Inside React — use the hook instead
 * const isEnabled = useFeatureFlag(FeatureFlag.RdpIntegration);
 */
export const isFeatureEnabled = (flag: FeatureFlag, marketplace?: MarketPlace): boolean => {
  const stageResult =
    { ...DEFAULT_FLAGS, ...STAGE_OVERRIDES[ProvisionedConstants.STAGE as STAGE] }[flag] ?? false;

  if (marketplace === undefined) return stageResult;

  return resolveMarketplace(stageResult, marketplace, MARKETPLACE_OVERRIDES[flag]);
};
